import { Autocomplete, Checkbox, FormControlLabel, TextField } from '@mui/material';
import DatePickerMoment from 'src/components/DatePickerMoment';
import fhirpath from 'fhirpath';
import { capitalCase } from 'change-case';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getFhirIdFromEntity } from 'src/utils/fhir';
import { capitalize } from 'src/utils/string';
import { useValueSets } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import SelectSearchMember from 'src/careflow/common/SelectSearchMember';

export default function CustomField({
  value,
  patient,
  element,
  errors,
  configurations,
  isLoading,
  selectedSearchMember,
  updateState,
}) {
  const elementValueType = getElementValueType(element);
  const valueKey = getValueKey(elementValueType);

  const fieldValue = getFieldValue(value, element.url, valueKey);

  const [, { find }] = useValueSets({
    map: ValueSetWrapper,
    autofetch: false,
  });

  const ValueSetAutocomplete = ({ element, value, updateStateVS }) => {
    const [options, setOptions] = useState<any>([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const valueSetId = useMemo(() => {
      const [valueSetCanonical] =
        fhirpath.evaluate(
          element,
          "differential.element.where(path = 'Extension.value[x]').binding.valueSet"
        ) ?? [];

      return valueSetCanonical ? getFhirIdFromEntity(valueSetCanonical) : undefined;
    }, [element]);

    const fetchValueSet = useCallback(async () => {
      if (!valueSetId || loaded) return;

      setLoading(true);
      try {
        const vs = await find({ _id: valueSetId });
        const vsAsList = vs?.[0]?.asList?.() ?? [];
        const vsMap = vsAsList.map((e) => ({
          coding: [e],
          text: e?.display,
        }));
        setOptions(vsMap);
        setLoaded(true);
      } finally {
        setLoading(false);
      }
    }, [valueSetId, loaded]);

    useEffect(() => {
      if (!open || !element) return;
      fetchValueSet();
    }, [open, element, fetchValueSet]);

    return (
      <Autocomplete
        fullWidth
        value={value}
        open={open}
        loading={loading}
        options={options}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        getOptionLabel={({ coding }) => coding?.[0]?.display ?? ''}
        isOptionEqualToValue={(option, value) =>
          option?.coding?.[0]?.code === value?.coding?.[0]?.code
        }
        onChange={(_: React.SyntheticEvent, values) => updateStateVS(values)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={capitalize(element.title)}
            placeholder={capitalize(element.title)}
          />
        )}
      />
    );
  };

  if (!valueKey) return null;

  return (
    <>
      {(() => {
        switch (elementValueType) {
          case 'string':
          case 'decimal':
            return (
              <TextField
                key={element.id}
                fullWidth
                id={element.name}
                name={element.name}
                label={capitalize(element.title)}
                placeholder={capitalize(element.title)}
                variant="outlined"
                value={fieldValue ?? ''}
                onChange={(ex) => {
                  const raw = ex.target.value;
                  if (elementValueType === 'decimal' && !isValidDecimalInput(raw)) return;
                  updateFieldValue(element.url, valueKey, raw, updateState);
                }}
              />
            );

          case 'dateTime':
            return (
              <DatePickerMoment
                key={element.id}
                value={fieldValue}
                disabled={isLoading}
                label={capitalize(element.title)}
                error={errors?.[valueKey]}
                onChange={(date) => {
                  if (date !== null && !date?.isValid?.()) {
                    updateState({
                      errors: { [valueKey]: 'Bad data' },
                    });
                    return;
                  }

                  updateState({
                    errors: { [valueKey]: null },
                  });

                  updateFieldValue(element.url, valueKey, date, updateState);
                }}
              />
            );

          case 'boolean':
            return (
              <FormControlLabel
                key={element.id}
                label={capitalize(element.title)}
                sx={{ marginLeft: 1 }}
                control={
                  <Checkbox
                    disableRipple
                    checked={!!fieldValue}
                    onChange={(ev) =>
                      updateFieldValue(element.url, valueKey, ev.target.checked, updateState)
                    }
                  />
                }
              />
            );

          case 'CodeableConcept':
            return (
              <ValueSetAutocomplete
                key={element.id}
                element={element}
                value={fieldValue}
                updateStateVS={(ev) => {
                  updateFieldValue(element.url, valueKey, ev, updateState);
                }}
              />
            );

          case 'Reference':
            return (
              <SelectSearchMember
                key={element.id}
                patient={patient ?? undefined}
                isLoading={isLoading || !patient}
                valueSelected={selectedSearchMember ?? fieldValue}
                labelText={element?.title ? capitalize(element?.title) : ''}
                options={configurations ?? []}
                selectedOption={'selectedSearchMember'}
                stateKey={'selectedSearchMember'}
                updateSelectedMember={(ev) => {
                  updateState({
                    ...ev,
                    searchMemberOption: { url: element.url, valueKey },
                  });
                }}
                updateState={(e) => {}}
              />
            );

          default:
            return <>{element.name}</>;
        }
      })()}
    </>
  );
}

const getValueKey = (type?: string) =>
  type ? `value${capitalCase(type)}`.replace(/\s+/g, '') : undefined;

const getFieldValue = (values: any[] | undefined, url: string, key?: string) => {
  if (!key) return '';
  return values?.find((v) => v.url === url)?.[key] ?? '';
};

export const updateFieldValue = (url: string, key: string, value: any, updateState: any) => {
  updateState((prev) => {
    const filtered = (prev.value ?? []).filter((v) => v.url !== url);

    return {
      ...prev,
      value: value == null ? filtered : [...filtered, { url, [key]: value }],
    };
  });
};

export const getElementValueType = (e: any) =>
  fhirpath.evaluate(e, "differential.element.where(path = 'Extension.value[x]').type.code")?.[0];

const isValidDecimalInput = (value: string) => /^(\d+(\.)?\d*|\.\d*)?$/.test(value);
